import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';

/**
 * Merch-N Cart Drawer
 *
 * Keeps Horizon's original cart drawer behavior and adds:
 *
 * - Cart-aware product recommendations
 * - Related recommendations for products currently in the cart
 * - Existing cart products excluded
 * - Duplicate recommendations excluded
 * - Direct Add to Cart from recommendation cards
 * - Automatic refresh after cart changes
 * - Compact 2-item recommendation carousel
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #recommendationAbortController = null;

  /** @type {number} */
  #recommendationRequestId = 0;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (
      this.closest('theme-drawer')
    );
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(
      StandardEvents.cartLinesUpdate,
      this.#handleCartLinesUpdate
    );

    this.#themeDrawer?.addEventListener(
      DrawerOpenEvent.eventName,
      this.#handleDrawerOpen
    );

    this.#bindRecommendationEvents();

    /*
     * The restore path can set [open] before this module loads.
     */
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }

    /*
     * The cart can already contain products when the
     * component first connects.
     */
    this.#loadRecommendations();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(
      StandardEvents.cartLinesUpdate,
      this.#handleCartLinesUpdate
    );

    this.#themeDrawer?.removeEventListener(
      DrawerOpenEvent.eventName,
      this.#handleDrawerOpen
    );

    this.#unbindRecommendationEvents();

    this.#recommendationAbortController?.abort();
    this.#recommendationAbortController = null;
  }


  /* =========================================================
     DRAWER
     ========================================================= */

  /**
   * Handles drawer opening.
   */
  #handleDrawerOpen = () => {
    this.#updateStickyState();

    this.#loadRecommendations();

    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')
        ?.shadowRoot
        ?.querySelector('#shopify-installments-cta');

      cta?.addEventListener(
        'click',
        () => this.#themeDrawer?.close(),
        { once: true }
      );
    });
  };


  /**
   * Handles cart line updates.
   *
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen =
      this.hasAttribute('auto-open') &&
      event.action === 'add' &&
      !this.#themeDrawer?.isOpen;

    const sourceModal =
      event.target instanceof Element
        ? event.target.closest('dialog:modal')
        : null;

    if (
      shouldAutoOpen &&
      !sourceModal &&
      !this.#isCartEmpty()
    ) {
      this.#themeDrawer?.open();
    }

    event.promise
      ?.then(({ detail }) => {
        const settle = () =>
          requestAnimationFrame(() => {
            this.#updateStickyState();
            this.#loadRecommendations();
          });

        if (!shouldAutoOpen || detail?.didError) {
          settle();
          return;
        }

        const openAndSettle = () => {
          if (!this.#themeDrawer?.isOpen) {
            this.#themeDrawer?.open();
          }

          settle();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener(
            'close',
            openAndSettle,
            { once: true }
          );
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn(
            '[cart-drawer] Event promise rejected:',
            error
          );
        }
      });
  };


  /**
   * Determines whether the cart drawer is empty.
   */
  #isCartEmpty() {
    return Boolean(
      this.querySelector('.cart-drawer--empty')
    );
  }


  /**
   * Updates summary positioning.
   *
   * When recommendations exist, the summary is placed
   * immediately after them instead of being pushed toward
   * the bottom of the drawer.
   */
  #updateStickyState() {
    const dialog = this.#dialog;

    if (!dialog) return;

    const content = dialog.querySelector(
      '.cart-drawer__content'
    );

    const summary = dialog.querySelector(
      '.cart-drawer__summary'
    );

    if (!content || !summary) {
      dialog.setAttribute(
        'cart-summary-sticky',
        'false'
      );

      return;
    }

    const recommendationRoot =
      dialog.querySelector(
        '[data-merchn-cart-recommendations]'
      );

    /*
     * Recommendations sit directly above the summary.
     *
     * Remove the automatic flex push and sticky positioning
     * so there cannot be a large blank area between the
     * recommendation carousel and Subtotal.
     */
    if (recommendationRoot) {
      dialog.setAttribute(
        'cart-summary-sticky',
        'false'
      );

      summary.style.position = 'static';
      summary.style.marginTop = '0';
      summary.style.maskImage = 'none';

      return;
    }

    /*
     * Preserve Horizon's original sticky behavior when
     * there are no recommendations.
     */
    const drawerHeight =
      dialog.getBoundingClientRect().height;

    const summaryHeight =
      summary.getBoundingClientRect().height;

    const ratio =
      summaryHeight / drawerHeight;

    dialog.setAttribute(
      'cart-summary-sticky',
      ratio > this.#summaryThreshold
        ? 'false'
        : 'true'
    );

    summary.style.position = '';
    summary.style.marginTop = '';
    summary.style.maskImage = '';
  };


  /* =========================================================
     RECOMMENDATIONS
     ========================================================= */

  /**
   * Finds the recommendation container.
   */
  #getRecommendationRoot() {
    return this.querySelector(
      '[data-merchn-cart-recommendations]'
    );
  }


  /**
   * Finds the recommendation products container.
   */
  #getRecommendationProducts() {
    return this.querySelector(
      '[data-merchn-recommendation-products]'
    );
  }


  /**
   * Bind delegated recommendation Add to Cart events.
   */
  #bindRecommendationEvents() {
    this.addEventListener(
      'click',
      this.#handleRecommendationClick
    );
  }


  /**
   * Remove delegated recommendation event listener.
   */
  #unbindRecommendationEvents() {
    this.removeEventListener(
      'click',
      this.#handleRecommendationClick
    );
  }


  /**
   * Handles Add to Cart buttons.
   *
   * @param {MouseEvent} event
   */
  #handleRecommendationClick = async (event) => {
    const target =
      event.target instanceof Element
        ? event.target.closest(
            '[data-merchn-recommendation-add]'
          )
        : null;

    if (!target) return;

    event.preventDefault();

    if (target.disabled) return;

    const variantId = Number(
      target.getAttribute(
        'data-merchn-variant-id'
      )
    );

    if (!variantId) return;

    target.disabled = true;

    const originalText =
      target.textContent;

    target.textContent = 'Adding…';

    try {
      const response = await fetch(
        `${window.Shopify.routes.root}cart/add.js`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            items: [
              {
                id: variantId,
                quantity: 1,
              },
            ],
          }),
        }
      );

      if (!response.ok) {
        throw new Error(
          `Cart add failed: ${response.status}`
        );
      }

      await response.json();

      await this.#refreshCart();

    } catch (error) {
      console.error(
        '[cart-drawer] Recommendation add failed:',
        error
      );

      target.disabled = false;
      target.textContent = originalText;
    }
  };


  /**
   * Refresh the cart drawer using Shopify's
   * Section Rendering API.
   *
   * IMPORTANT:
   * The refreshed cart HTML is inserted into the existing
   * cart-drawer-component. Horizon's cart-items-component
   * must be fully upgraded before the drawer continues.
   */
  async #refreshCart() {
    const sectionId =
      'cart-drawer-section';

    const url =
      `${window.location.pathname}?sections=${encodeURIComponent(sectionId)}`;

    const response =
      await fetch(url, {
        headers: {
          Accept:
            'application/json',
        },
      });

    if (!response.ok) {
      throw new Error(
        `Cart refresh failed: ${response.status}`
      );
    }

    const sections =
      await response.json();

    const sectionHtml =
      sections[sectionId];

    if (!sectionHtml) {
      throw new Error(
        'Cart drawer section HTML was not returned.'
      );
    }

    const parser =
      new DOMParser();

    const doc =
      parser.parseFromString(
        sectionHtml,
        'text/html'
      );

    const incomingDrawer =
      doc.querySelector(
        '#cart-drawer'
      );

    const currentDrawer =
      document.querySelector(
        '#cart-drawer'
      );

    if (!incomingDrawer || !currentDrawer) {
      window.location.reload();
      return;
    }

    const incomingComponent =
      incomingDrawer.querySelector(
        'cart-drawer-component'
      );

    const currentComponent =
      currentDrawer.querySelector(
        'cart-drawer-component'
      );

    if (
      incomingComponent &&
      currentComponent
    ) {
      /*
       * Keep the existing cart-drawer-component alive.
       * Only its inner content is replaced.
       */
      currentComponent.innerHTML =
        incomingComponent.innerHTML;

      /*
       * IMPORTANT:
       *
       * cart-products.liquid loads Horizon's
       * cart-items-component and quantity-selector
       * modules with <script type="module">.
       *
       * Those scripts do not execute again when the
       * markup is inserted through innerHTML.
       *
       * Make sure both custom elements are defined
       * before the freshly rendered cart is interacted
       * with. This fixes Remove / quantity controls after
       * product-page and collection-page adds.
       */
      const moduleScripts =
        Array.from(
          incomingComponent.querySelectorAll(
            'script[type="module"][src]'
          )
        );

      for (
        const script of moduleScripts
      ) {
        try {
          await import(
            /** @type {HTMLScriptElement} */ (
              script
            ).src
          );
        } catch (error) {
          console.warn(
            '[cart-drawer] Could not initialize cart module:',
            error
          );
        }
      }

      if (
        customElements.get(
          'cart-items-component'
        )
      ) {
        await customElements.whenDefined(
          'cart-items-component'
        );
      }

      if (
        customElements.get(
          'quantity-selector-component'
        )
      ) {
        await customElements.whenDefined(
          'quantity-selector-component'
        );
      }
    }

    document.dispatchEvent(
      new CustomEvent(
        StandardEvents.cartLinesUpdate,
        {
          bubbles: true,
          detail: {
            action: 'add',
          },
        }
      )
    );

    this.#updateStickyState();

    await this.#loadRecommendations();
  };


  /**
   * Loads recommendations based on every product
   * currently in the cart.
   */
  async #loadRecommendations() {
    const root =
      this.#getRecommendationRoot();

    const productsContainer =
      this.#getRecommendationProducts();

    if (
      !root ||
      !productsContainer
    ) {
      return;
    }

    /*
     * Keep summary positioned correctly while the
     * recommendation request is running.
     */
    this.#updateStickyState();

    const productIds =
      this.#getCartProductIds(root);

    if (!productIds.length) {
      root.remove();

      this.#updateStickyState();

      return;
    }

    this.#recommendationAbortController?.abort();

    const controller =
      new AbortController();

    this.#recommendationAbortController =
      controller;

    const requestId =
      ++this.#recommendationRequestId;

    productsContainer.innerHTML = '';

    try {
      const recommendationMap =
        new Map();

      /*
       * Get related products for every product in cart.
       */
      for (const productId of productIds) {
        if (controller.signal.aborted) {
          return;
        }

        const recommendations =
          await this.#fetchRecommendations(
            productId,
            controller.signal
          );

        for (
          const product of recommendations
        ) {
          if (
            !product?.id ||
            !product?.handle
          ) {
            continue;
          }

          const key =
            String(product.id);

          if (
            !recommendationMap.has(key)
          ) {
            recommendationMap.set(
              key,
              product
            );
          }
        }
      }

      if (
        controller.signal.aborted ||
        requestId !== this.#recommendationRequestId
      ) {
        return;
      }

      /*
       * Fetch the latest cart so products added
       * moments ago are never recommended.
       */
      const cart =
        await this.#fetchCurrentCart(
          controller.signal
        );

      const cartProductIds =
        new Set(
          (cart.items || []).map(
            (item) =>
              String(item.product_id)
          )
        );

      const cartVariantIds =
        new Set(
          (cart.items || []).map(
            (item) =>
              String(item.variant_id)
          )
        );

      const filtered =
        Array.from(
          recommendationMap.values()
        ).filter((product) => {
          if (
            cartProductIds.has(
              String(product.id)
            )
          ) {
            return false;
          }

          const variant =
            this.#getAvailableVariant(
              product
            );

          if (!variant) {
            return false;
          }

          if (
            cartVariantIds.has(
              String(variant.id)
            )
          ) {
            return false;
          }

          return true;
        });

      /*
       * Four products are loaded.
       *
       * CSS/inline sizing makes exactly TWO visible,
       * with the remaining products available by swipe.
       */
      const recommendations =
        filtered.slice(0, 4);

      if (!recommendations.length) {
        root.remove();

        this.#updateStickyState();

        return;
      }

      productsContainer.innerHTML =
        recommendations
          .map(
            (product) =>
              this.#renderRecommendation(
                product
              )
          )
          .join('');

      /*
       * Re-apply the summary positioning after the
       * recommendation cards have been rendered.
       */
      this.#updateStickyState();

    } catch (error) {
      if (
        error?.name === 'AbortError'
      ) {
        return;
      }

      console.warn(
        '[cart-drawer] Could not load recommendations:',
        error
      );

      root.remove();

      this.#updateStickyState();
    }
  };


  /**
   * Gets product IDs from the recommendation container.
   *
   * @param {HTMLElement} root
   */
  #getCartProductIds(root) {
    const value =
      root.getAttribute(
        'data-cart-product-ids'
      );

    if (!value) {
      return [];
    }

    return [
      ...new Set(
        value
          .split(',')
          .map((id) => id.trim())
          .filter(Boolean)
      ),
    ];
  };


  /**
   * Shopify related product recommendations.
   *
   * @param {string} productId
   * @param {AbortSignal} signal
   */
  async #fetchRecommendations(
    productId,
    signal
  ) {
    const url =
      `${window.Shopify.routes.root}recommendations/products.json?product_id=${encodeURIComponent(productId)}&limit=10&intent=related`;

    const response =
      await fetch(url, {
        signal,
        headers: {
          Accept: 'application/json',
        },
      });

    if (!response.ok) {
      throw new Error(
        `Recommendation request failed: ${response.status}`
      );
    }

    const data =
      await response.json();

    return Array.isArray(
      data.products
    )
      ? data.products
      : [];
  };


  /**
   * Fetches the current cart JSON.
   *
   * @param {AbortSignal} signal
   */
  async #fetchCurrentCart(
    signal
  ) {
    const response =
      await fetch(
        `${window.Shopify.routes.root}cart.js`,
        {
          signal,
          headers: {
            Accept: 'application/json',
          },
        }
      );

    if (!response.ok) {
      throw new Error(
        `Cart request failed: ${response.status}`
      );
    }

    return response.json();
  };


  /**
   * Find a purchasable variant.
   *
   * @param {object} product
   */
  #getAvailableVariant(product) {
    if (
      !Array.isArray(
        product.variants
      )
    ) {
      return null;
    }

    return (
      product.variants.find(
        (variant) =>
          variant.available
      ) || null
    );
  };


  /**
   * Creates recommendation card markup.
   *
   * IMPORTANT:
   * The + Add to Cart button is now OUTSIDE the
   * hidden information container, so it remains visible.
   *
   * Each card is explicitly sized to 50% minus the gap,
   * giving exactly TWO visible products.
   *
   * @param {object} product
   */
  #renderRecommendation(product) {
    const variant =
      this.#getAvailableVariant(
        product
      );

    if (!variant) {
      return '';
    }

    const image =
      product.featured_image ||
      product.images?.[0] ||
      '';

    const productTitle =
      product.title || '';

    const productUrl =
      `${window.Shopify.routes.root}products/${encodeURIComponent(product.handle)}`;

    const imageMarkup =
      image
        ? `
          <img
            class="merchn-cart-recommendation__image"
            src="${this.#escapeAttribute(image)}"
            alt="${this.#escapeAttribute(productTitle)}"
            loading="lazy"
            width="300"
            height="300"
          >
        `
        : `
          <div
            class="merchn-cart-recommendation__image"
            aria-hidden="true"
          ></div>
        `;

    return `
      <article
        class="merchn-cart-recommendation"
        data-merchn-recommendation
        data-product-id="${this.#escapeAttribute(product.id)}"
        style="flex: 0 0 calc((100% - 9px) / 2);"
      >

        <a
          href="${this.#escapeAttribute(productUrl)}"
          class="merchn-cart-recommendation__link"
          aria-label="${this.#escapeAttribute(productTitle)}"
        >
          ${imageMarkup}
        </a>

        <button
          type="button"
          class="merchn-cart-recommendation__button"
          data-merchn-recommendation-add
          data-merchn-variant-id="${this.#escapeAttribute(variant.id)}"
          aria-label="Add ${this.#escapeAttribute(productTitle)} to cart"
        >
          <span aria-hidden="true">+</span>
        </button>

      </article>
    `;
  };


  /**
   * Escape HTML text.
   *
   * @param {unknown} value
   */
  #escapeHtml(value) {
    return String(value)
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');
  };


  /**
   * Escape an attribute value.
   *
   * @param {unknown} value
   */
  #escapeAttribute(value) {
    return this.#escapeHtml(value);
  };
}


if (
  !customElements.get(
    'cart-drawer-component'
  )
) {
  customElements.define(
    'cart-drawer-component',
    CartDrawerComponent
  );
}